import { useTranslation, useServices } from 'cng-web-lib'
import moment from 'moment-timezone'
import React, { useRef, useState } from 'react'
import { Button, makeStyles, useTheme } from '@material-ui/core'
import UaeMpciApiUrls from 'src/apiUrls/UaeMpciApiUrls'
import Namespace from 'src/constants/locale/Namespace'
import MpciTable from '../Common/components/MpciTable'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import pathMap from 'src/paths/pathMap'
import TruncCell from '../Common/components/TruncCell'
import ManifestIdsCell from '../Common/components/ManifestIdsCell'
import UploadService from './UploadService'
import SubmitDraftsDialog from './SubmitDraftsDialog'
import EditDraftsDialog from './EditDraftsDialog'
import DeleteDraftsDialog from './DeleteDraftsDialog'

const useStyles = makeStyles(() => ({
  chip: {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.2,
    whiteSpace: 'nowrap'
  }
}))

function UploadHistoryPage(props) {
  const { history, location, showNotification } = props
  const tableRef = useRef(null)
  const { translate } = useTranslation([Namespace.UI_COMPONENT, Namespace.UAE_MPCI])
  const theme = useTheme()
  const classes = useStyles()
  const { securedSendRequest } = useServices()

  // ─── Dialog state ────────────────────────────────────────────────────────
  const [activeRow, setActiveRow] = useState(null)
  const [submitOpen, setSubmitOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  function openDialog(row, dialog) {
    setActiveRow(row)
    if (dialog === 'submit') setSubmitOpen(true)
    else if (dialog === 'edit') setEditOpen(true)
    else if (dialog === 'delete') setDeleteOpen(true)
  }

  function closeAll() {
    if (submitting) return
    setSubmitOpen(false)
    setEditOpen(false)
    setDeleteOpen(false)
    setActiveRow(null)
  }

  function refreshTable() {
    if (tableRef.current?.performRefresh) tableRef.current.performRefresh()
  }

  // ─── Submit Drafts ───────────────────────────────────────────────────────
  function handleSubmitDrafts() {
    if (!activeRow) return
    setSubmitting(true)
    console.log('[UploadHistoryPage] submitDrafts — fileUploadId:', activeRow.id)
    UploadService.submitDrafts(
      activeRow.id,
      securedSendRequest,
      () => {
        setSubmitting(false)
        setSubmitOpen(false)
        setActiveRow(null)
        if (showNotification) showNotification('success', 'Draft(s) submitted to NAIC successfully.')
        refreshTable()
      },
      (error) => {
        setSubmitting(false)
        const msg = error?.response?.data?.message || 'Error encountered submitting draft(s)'
        if (showNotification) showNotification('error', msg)
      }
    )
  }

  // ─── Edit Drafts ─────────────────────────────────────────────────────────
  function handleEditDrafts(file) {
    if (!activeRow || !file) return
    setSubmitting(true)
    console.log('[UploadHistoryPage] editDrafts — fileUploadId:', activeRow.id, '| file:', file.name)
    const formData = new FormData()
    formData.append('fileUploadId', activeRow.id)
    formData.append('file', file)
    UploadService.editDrafts(
      formData,
      securedSendRequest,
      () => {
        setSubmitting(false)
        setEditOpen(false)
        setActiveRow(null)
        if (showNotification) showNotification('success', 'Draft(s) updated successfully.')
        refreshTable()
      },
      (error) => {
        setSubmitting(false)
        const msg = error?.response?.data?.message || 'Error encountered uploading/editing draft(s)'
        if (showNotification) showNotification('error', msg)
      }
    )
  }

  // ─── Delete Drafts ───────────────────────────────────────────────────────
  function handleDeleteDrafts() {
    if (!activeRow) return
    setSubmitting(true)
    console.log('[UploadHistoryPage] deleteDrafts — fileUploadId:', activeRow.id)
    UploadService.deleteDrafts(
      activeRow.id,
      securedSendRequest,
      () => {
        setSubmitting(false)
        setDeleteOpen(false)
        setActiveRow(null)
        if (showNotification) showNotification('success', 'Draft(s) deleted successfully.')
        refreshTable()
      },
      (error) => {
        setSubmitting(false)
        const msg = error?.response?.data?.message || 'Error encountered deleting draft(s)'
        if (showNotification) showNotification('error', msg)
      }
    )
  }

  // Status badge map — RECV/PROC/COMP/FAIL + long-form aliases
  const statusColorMap = {
    RECV:             { bg: theme.palette.warning.light, text: theme.palette.warning.dark,  label: 'Received' },
    RECEIVED:         { bg: theme.palette.warning.light, text: theme.palette.warning.dark,  label: 'Received' },
    PROC:             { bg: theme.palette.info.light,    text: theme.palette.info.dark,     label: 'Processing' },
    PROCESSING:       { bg: theme.palette.info.light,    text: theme.palette.info.dark,     label: 'Processing' },
    COMP:             { bg: theme.palette.success.light, text: theme.palette.success.dark,  label: "Bill(s) Created" },
    COMPLETED:        { bg: theme.palette.success.light, text: theme.palette.success.dark,  label: "Bill(s) Created" },
    BILLS_CREATED:    { bg: theme.palette.success.light, text: theme.palette.success.dark,  label: "Bill(s) Created" },
    FAIL:             { bg: theme.palette.error.light,   text: theme.palette.error.dark,    label: 'Processing Error' },
    FAILED:           { bg: theme.palette.error.light,   text: theme.palette.error.dark,    label: 'Processing Error' },
    PROCESSING_ERROR: { bg: theme.palette.error.light,   text: theme.palette.error.dark,    label: 'Processing Error' }
  }

    const columns = [
    { field: 'id', sortKey: 'id', title: 'File Upload System ID', render: (row) => <TruncCell value={row.id} /> },
    { field: 'fileName', sortKey: 'fileName', title: 'File Name' },
    {
      field: 'manifestHeaderId',
      sortKey: 'manifestHeaderId',
      title: 'Master/House Bill System ID(s)',
      render: (row) => <ManifestIdsCell ids={row.manifestHeaderIds} />
    },
        {
      field: 'status',
      sortKey: 'status',
      title: 'Status',
      render: (row) => {
        const c = statusColorMap[row.status] || { bg: theme.palette.background.sectionOddBg, text: theme.palette.text.secondary, label: row.status }
        return (
          <span className={classes.chip} style={{ backgroundColor: c.bg, color: c.text }}>
            {c.label}
          </span>
        )
      }
    },
    { field: 'errorFilePath', sortKey: 'errorFilePath', title: 'Error Log', render: (row) => {
      const val = row.errorFilePath
      // Sanitise: empty, null string, or mojibake of em dash (U+0393 U+00C7 U+00F6) all render as em dash
      const isEmpty = !val || val === 'null' || val.trim() === ''
      const isMojibake = typeof val === 'string' && /\u0393\u00c7\u00f6/.test(val)
      if (isEmpty || isMojibake) return <span>{'\u2014'}</span>
      return val
    } },
        { field: 'createdDate',
      sortKey: 'createdDate',
      title: 'Created Date',
      render: (row) => row.createdDate && moment(row.createdDate).format('D MMM YYYY, HH:mm')
    }
  ]

  // ─── Actions column ───────────────────────────────────────────────────────
  const isCompStatus = (row) => ['COMP', 'COMPLETED', 'BILLS_CREATED'].includes(row.status)

    const filters = [
    { name: 'id',                label: 'File Upload System ID',       type: 'text' },
    { name: 'fileName',          label: 'File Name',                   type: 'text' },
    { name: 'manifestHeaderId',  label: 'Master/House Bill System ID', type: 'text' },
    { name: 'status',             label: 'Status',                type: 'select', options: [
      { value: 'RECV', label: 'Received' },
      { value: 'PROC', label: 'Processing' },
      { value: 'COMP', label: "Bill(s) Created" },
      { value: 'FAIL', label: 'Processing Error' }
    ]},
    { name: 'createdDate', label: 'Created Date', type: 'daterange' }
  ]

return (
  <>
    <MpciTable
      url={UaeMpciApiUrls.EXCEL_UPLOAD_LIST}
      columns={columns}
      filters={filters}
      actions={[
        {
          component: () => (
            <Button
              color="primary"
              variant="contained"
              size="medium"
              startIcon={<FontAwesomeIcon icon={['fal', 'upload']} />}
              onClick={() => history.push(pathMap.UAE_MPCI_FILE_UPLOAD)}
            >
              Upload File
            </Button>
          )
        }
      ]}
      rowActions={[
        {
          icon: <FontAwesomeIcon icon={['fal', 'download']} />,
          label: 'Download Report',
          onClick: (row) => {},
          disabled: (row) =>
            !['COMP', 'COMPLETED', 'BILLS_CREATED'].includes(row.status)
        },
        {
          icon: <FontAwesomeIcon icon={['fal', 'paper-plane']} />,
          label: 'Submit to NAIC',
          onClick: (row) => openDialog(row, 'submit'),
          hidden: (row) => !isCompStatus(row)
        },
        {
          icon: <FontAwesomeIcon icon={['fal', 'pen-to-square']} />,
          label: 'Edit Draft',
          onClick: (row) => openDialog(row, 'edit'),
          hidden: (row) => !isCompStatus(row)
        },
        {
          icon: <FontAwesomeIcon icon={['fal', 'trash-can']} />,
          label: 'Delete Draft',
          onClick: (row) => openDialog(row, 'delete'),
          hidden: (row) => !isCompStatus(row)
        }
      ]}
      defaultSort={{ field: 'createdDate', direction: 'DESC' }}
      ref={tableRef}
    />

    {/* Draft Action Dialogs */}
    <SubmitDraftsDialog
      open={submitOpen}
      row={activeRow}
      onClose={closeAll}
      onConfirm={handleSubmitDrafts}
      submitting={submitting}
    />

    <EditDraftsDialog
      open={editOpen}
      row={activeRow}
      onClose={closeAll}
      onConfirm={handleEditDrafts}
      submitting={submitting}
    />

    <DeleteDraftsDialog
      open={deleteOpen}
      row={activeRow}
      onClose={closeAll}
      onConfirm={handleDeleteDrafts}
      submitting={submitting}
    />
  </>
);
}

export default UploadHistoryPage